import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { CdkTrapFocus } from '@angular/cdk/a11y';
import { ButtonComponent } from '@shared/components/buttons/button/button.component';
import { PassoDoTutorial } from './tutorial.model';

/**
 * O balão de um passo do tutorial.
 *
 * **Por que é componente Angular, e não string de HTML injetada no overlay:**
 * o conteúdo deste balão vai deixar de ser texto fixo do tour e passar a ser
 * conteúdo renderizado da base de conhecimento (artigo, imagem, link para o
 * suporte). String de HTML obrigaria a sanitizar à mão e fecharia a porta para
 * qualquer componente filho — aqui basta acrescentar o que vier.
 *
 * **Ele não conhece o `TutorialService`.** Recebe o passo por `@Input` e
 * devolve intenção por `@Output`. Duas razões: não fecha ciclo de import com o
 * serviço (que é quem instancia este componente no overlay), e o balão fica
 * montável em teste sem nenhuma máquina de passos por trás.
 *
 * Nos formulários o balão é não modal: o foco permanece nos campos e nas
 * opções abertas pelo usuário. Nos tours de apresentação, o CDK mantém
 * o foco nos controles do guia. O serviço trata o encerramento por Esc.
 */
@Component({
  selector: 'mg-tutorial-passo',
  templateUrl: './passo.component.html',
  styleUrls: ['./passo.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CdkTrapFocus, ButtonComponent],
})
export class PassoComponent {
  @Input({ required: true }) passo!: PassoDoTutorial;

  /** Base 0 — o template soma 1 para falar com gente. */
  @Input({ required: true }) indice = 0;

  @Input({ required: true }) total = 0;

  /** Começa sem capturar foco até o serviço informar o tipo de apresentação. */
  @Input() interativo = true;

  @Output() pular = new EventEmitter<void>();
  @Output() anterior = new EventEmitter<void>();
  @Output() proximo = new EventEmitter<void>();

  /** No último passo o botão de avançar deixa de prometer que há mais tela. */
  protected get ultimo(): boolean {
    return this.indice >= this.total - 1;
  }

  protected get primeiro(): boolean {
    return this.indice === 0;
  }

  /**
   * Os pontinhos de progresso. É um vetor de índices, e não `total` cru, porque
   * `@for` precisa de algo iterável com chave estável para o `track`.
   */
  protected get pontos(): number[] {
    return Array.from({ length: this.total }, (_, posicao) => posicao);
  }
}
